import Vue from 'vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faEnvelope, faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faFacebookF,
  faFacebookMessenger,
  faTwitter,
  faLinkedinIn,
} from '@fortawesome/free-brands-svg-icons';
import { VueSpinners } from '@saeris/vue-spinners';
import Notifications from 'vue-notification';
import JsonCSV from 'vue-json-csv';
import Clipboard from 'v-clipboard';
import VueI18n from 'vue-i18n';

import App from './App.vue';
import router from './router';
import store from './store';
import './styles/index.css';
import { axiosConfig } from './api';
import { i18n as i18nMessages } from './i18n';
import { hasAdminAccess, isAdminPath, isAuthPath, authUser, canIAccess } from './utils';

library.add([faFacebookF, faFacebookMessenger, faTwitter, faLinkedinIn, faEnvelope, faLink]);

Vue.config.productionTip = false;

Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('downloadCsv', JsonCSV);

Vue.use(axiosConfig, { store, router });
Vue.use(VueSpinners);
Vue.use(Notifications);
Vue.use(Clipboard);
Vue.use(VueI18n);

Object.defineProperty(Vue.prototype, '$canI', {
  get() {
    const user = store.state.auth.user;
    return {
      create: canIAccess(user.role, this.$route.name, 'create'),
      update: canIAccess(user.role, this.$route.name, 'update'),
      delete: canIAccess(user.role, this.$route.name, 'delete'),
    };
  },
});

Object.defineProperty(Vue.prototype, '$user', {
  get() {
    return authUser(this.$store.state, this.$route.path);
  },
});

Object.defineProperty(Vue.prototype, '$language', {
  get() {
    return this.$store.state.language;
  },
});

const i18n = new VueI18n({
  locale: 'en',
  messages: i18nMessages(),
});

// redirect to customer || admin login screen based on current path and NOT user
router.beforeEach((to, _, next) => {
  const user = store.state.auth.user;
  if (to.matched.some(r => r.meta.auth === true) && !user) {
    return isAdminPath(to.path) ? next({ path: '/admin/login' }) : next({ path: '/auth/login' });
  }
  next();
});

// redirect back to / path if attempting to access /auth/* paths when logged in
router.beforeEach((to, _, next) => {
  const user = store.state.auth.user;

  if (user && hasAdminAccess(user) && isAuthPath(to.path)) {
    return next({ path: '/admin' });
  }

  if (user && !hasAdminAccess(user) && isAuthPath(to.path)) {
    return next({ path: '/' });
  }

  next();
});

// redirect back to 'home' path if attempting unautorized pages based on user type
router.beforeEach((to, _, next) => {
  const user = store.state.auth.user;
  const impersonatedUser = store.state.auth.impersonatedUser;

  const onAdminPath = isAdminPath(to.path);
  const protectedAdminPath = to.matched.some(r => r.meta.auth && r.meta.admin);
  const protectedCustomerPath = to.matched.some(r => r.meta.auth && r.meta.customer);

  if ((onAdminPath || protectedAdminPath) && user && !hasAdminAccess(user)) {
    return next({ path: '/' });
  }

  if (protectedCustomerPath && user && hasAdminAccess(user) && !impersonatedUser) {
    return next({ path: '/admin' });
  }

  next();
});

Vue.config.errorHandler = (error, vue) => {
  return vue.$notify({
    group: 'error',
    type: 'error',
    title: 'Error',
    text: error,
  });
};

new Vue({
  router,
  store,
  i18n,
  render: h => h(App),
}).$mount('#app');
